/**
 * Recovery for a lazy route whose chunk no longer exists on the server: a deploy
 * while the tab was open replaces every hashed filename, so the `import()` behind
 * a `React.lazy` route 404s and throws in render. The module can never load in
 * this document, and only a reload picks up the new entry point.
 *
 * @module lib/stale-chunk
 */

const RELOAD_AT_KEY = "transfr:dashboard:chunkReloadAt";

/** Shortest gap between two reload attempts in one tab. */
const RELOAD_COOLDOWN_MS = 10_000;

/**
 * Browser messages for a module script that could not be fetched or executed,
 * lower-cased. Each engine words it differently and none carries a code.
 */
const STALE_CHUNK_MESSAGES = [
  "failed to fetch dynamically imported module",
  "error loading dynamically imported module",
  "importing a module script failed",
  "failed to load module script",
  "unable to preload css",
];

/**
 * @param {unknown} error
 * @returns {boolean}
 */
export function isStaleChunkError(error) {
  const message = typeof error === "string" ? error : error?.message;
  if (typeof message !== "string") {
    return false;
  }
  const normalized = message.toLowerCase();
  return STALE_CHUNK_MESSAGES.some((known) => normalized.includes(known));
}

/**
 * Whether this document may reload now, stamping the attempt. Rate-limited so a
 * chunk that is genuinely gone from every deploy falls through to the error
 * boundary instead of reloading in a loop, while a second deploy later in the
 * same session can still recover.
 *
 * @returns {boolean}
 */
export function claimStaleChunkReload() {
  let last;
  try {
    last = Number(sessionStorage.getItem(RELOAD_AT_KEY));
  } catch {
    // A blocked storage can't rate-limit, so it doesn't get to reload either.
    return false;
  }
  const now = Date.now();
  if (Number.isFinite(last) && now - last < RELOAD_COOLDOWN_MS) {
    return false;
  }
  try {
    sessionStorage.setItem(RELOAD_AT_KEY, String(now));
  } catch {
    return false;
  }
  return true;
}

/**
 * Reloads when `error` is a stale chunk and the cooldown allows it.
 *
 * @param {unknown} error
 * @returns {boolean} Whether a reload was started, so a caller can skip the
 *   error state it would otherwise render.
 */
export function recoverFromStaleChunk(error) {
  if (!isStaleChunkError(error) || !claimStaleChunkReload()) {
    return false;
  }
  globalThis.location.reload();
  return true;
}

/**
 * Handles Vite's own signal for a failed module preload, which fires before the
 * import rejects. Cancelling it keeps the rejection from reaching the error
 * boundary, so the reload happens without the error page flashing first.
 */
export function initStaleChunkRecovery() {
  globalThis.addEventListener("vite:preloadError", (event) => {
    if (claimStaleChunkReload()) {
      event.preventDefault();
      globalThis.location.reload();
    }
  });
}
